import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CommonButton } from 'gemlux-common-components';
import { theme } from '../../../theme/theme';
import CustomButton from '../../V1/CustomButton/CustomButton';
import { userActions } from '../../../redux/user/userSlice';
import { SquareInputBox } from '../../SquareInputBox/SquareInputBox';
import { RootState } from '../../../redux/store';
import CustomModal from '../../V1/CustomModal/CustomModal';
import AlertIcon from '../../../assets/img/alert-yellow.svg';
import AlertSvg from '../../../assets/img/alert.svg';

export default function ChangePin(): React.ReactElement {
  const dispatch = useDispatch();
  const componentStatus = useSelector((state: RootState) => state.user.componentStatus);

  const [newPin, setNewPin] = useState<string[]>(Array(4).fill(''));
  const [confirmPin, setConfirmPin] = useState<string[]>(Array(4).fill(''));
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [pinError, setPinError] = useState<string>('');

  const pinFields = [
    { id: 'newPin-1', name: 'newPin0' },
    { id: 'newPin-2', name: 'newPin1' },
    { id: 'newPin-3', name: 'newPin2' },
    { id: 'newPin-4', name: 'newPin3' },
  ];

  const handlePinChange = (
    setPin: React.Dispatch<React.SetStateAction<string[]>>,
    pin: string[],
    index: number,
    value: string,
  ) => {
    if (/^\d?$/.test(value)) {
      const newPinArray = [...pin];
      newPinArray[index] = value;
      setPin(newPinArray);

      if (value && index < pin.length - 1) {
        const nextInput = document.querySelector<HTMLInputElement>(
          `input[name='${pin === newPin ? 'newPin' : 'confirmPin'}${index + 1}']`,
        );
        if (nextInput) nextInput.focus();
      }
    }

    if (pinError) {
      setPinError('');
    }
  };

  const handleUpdatePin = () => {
    const newPinStr = newPin.join('');
    const confirmPinStr = confirmPin.join('');

    if (newPinStr !== confirmPinStr) {
      setPinError('PIN do not match!');
      return;
    }

    setIsConfirmationOpen(true);
  };

  const handleConfirmUpdate = () => {
    const newPinStr = newPin.join('');
    dispatch(userActions.changePinAsync({ newPin: newPinStr }));
    setIsConfirmationOpen(false);
  };

  useEffect(() => {
    if (componentStatus.successModalOpen) {
      dispatch(userActions.clearComponentStatus(['successModalOpen']));
      setNewPin(Array(4).fill(''));
      setConfirmPin(Array(4).fill(''));
      setPinError('');
    }
    if (componentStatus.error) {
      setPinError(`Error: ${componentStatus.error}`);
      dispatch(userActions.clearComponentStatus(['error']));
    }
  }, [componentStatus, dispatch]);

  const isUpdateDisabled = newPin.some((digit) => digit === '') || confirmPin.some((digit) => digit === '');

  const enterKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !isUpdateDisabled) {
      event.preventDefault();

      handleUpdatePin(); // Only trigger form submission if the button is enabled
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', enterKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', enterKeyDownHandler);
    };
  }, [isUpdateDisabled]);

  return (
    <Grid
      container
      spacing={2}
      marginTop={1}
      marginLeft={1}
      sx={{
        border: `1px solid ${theme.palette.shades.grayBorder}`,
        borderRadius: '8px',
        display: 'flex',
        width: '30%',
        flexDirection: 'column',
        background: 'var(--Background-bg-base, #FFF)',
      }}
    >
      <Grid item xs={12} display="flex" flexDirection="column">
        <Stack spacing={1} mb="24px" paddingRight={2}>
          <Typography variant="textBase" color="#525E6F">
            To ensure security during offline scans, please create a 4-digit PIN to access your Krista/Kroma device. You
            can set it up later if needed.
          </Typography>
        </Stack>

        <Grid container gap={1.5} flexDirection="column">
          <Typography variant="textBase500">New PIN</Typography>
          <Box display="flex" columnGap={3}>
            {pinFields.map((field, index) => (
              <SquareInputBox
                key={field.id}
                theme={theme}
                height="40px"
                width="40px"
                autoComplete="off"
                autoFocus={index === 0}
                type="text"
                name={`newPin${index}`}
                value={newPin[index]}
                onChange={(e) => handlePinChange(setNewPin, newPin, index, e.target.value)}
                variant="outlined"
              />
            ))}
          </Box>
        </Grid>

        <Grid container gap={1.5} flexDirection="column" paddingTop="5%">
          <Typography variant="textBase500">Confirm PIN</Typography>
          <Box display="flex" columnGap={3}>
            {pinFields.map((field, index) => (
              <SquareInputBox
                key={field.id}
                theme={theme}
                height="40px"
                width="40px"
                autoComplete="off"
                type="text"
                name={`confirmPin${index}`}
                value={confirmPin[index]}
                onChange={(e) => handlePinChange(setConfirmPin, confirmPin, index, e.target.value)}
                variant="outlined"
              />
            ))}
          </Box>
          {pinError && (
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box src={AlertSvg} component="img" />
              <Typography variant="footnoteRegular2" color="#DF0C3D">
                {pinError}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12} display="flex" justifyContent="flex-end" padding={3}>
        <Box>
          <CustomButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpdatePin}
            disabled={isUpdateDisabled || Boolean(componentStatus.isSubmitting)}
          >
            {componentStatus.isSubmitting ? 'Updating...' : 'Update'}
          </CustomButton>
        </Box>
      </Grid>

      <CustomModal
        modal
        open={isConfirmationOpen}
        handleClose={() => setIsConfirmationOpen(false)}
        Icon={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '32px',
              background: 'var(--Warning-warning-200, #FFED9E)',
            }}
          >
            <Box
              component="img"
              src={AlertIcon}
              alt="Delete Icon"
              sx={{
                width: '40px',
                height: '40px',
              }}
            />
          </Box>
        }
        title="Updating KRISTA™/KROMA™ PIN"
        subTitle="Are you sure you want to update KRISTA™/KROMA™ PIN?"
      >
        <Box display="flex" flexDirection="column" gap="8px" justifyContent="end" alignItems="center" mt={1}>
          <CommonButton
            variant="contained"
            data-testid="confirm-update-button"
            sx={{ width: '100%', backgroundColor: theme.palette.shades.primaryBrightDark, marginTop: '10px' }}
            onClick={handleConfirmUpdate}
          >
            Yes, Update
          </CommonButton>
          <CommonButton
            variant="outlined"
            data-testid="cancel-button"
            sx={{
              width: '100%',
              color: theme.palette.common.black,
              border: `1px solid ${theme.palette.shades.grayBorder}`,
            }}
            onClick={() => setIsConfirmationOpen(false)}
          >
            Cancel
          </CommonButton>
        </Box>
      </CustomModal>
    </Grid>
  );
}
